import React from "react";
import { Ellipsis } from "lucide-react";
import { cn } from "../../lib/utils.js";
import type { TriggerSize } from "../../lib/trigger-size.js";
import { Button, type ButtonShape } from "../../atoms/Button/index.js";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover/index.js";
import { ActionMenuItems } from "./ActionMenuItems.js";

/* ---------------- ActionMenu ----------------
   The overflow ("kebab") menu for a row, card or entity header: a trigger, a
   list of actions, and — the part worth sharing — a first-class way to say why
   an action is unavailable.

   `disabledReason` is the whole point. An unavailable action renders disabled
   with its reason as VISIBLE secondary text, wired to the item via
   aria-describedby. Presence of `disabledReason` is what disables the item, so
   "disabled with no stated reason" is unrepresentable rather than merely
   discouraged. Do not reach for `title`: a disabled control does not reliably
   fire hover, so a tooltip hides the explanation exactly where it is needed,
   from pointer and AT users alike.

   Data-blind: it takes items and callbacks. Gating actions on a caller's role
   is the consumer's job — module permission hooks stay in feature packages. */

export interface ActionMenuItem {
  readonly id: string;
  readonly label: string;
  readonly icon?: React.ElementType;
  /** Semantic treatment for destructive actions such as Delete. */
  readonly tone?: "neutral" | "critical";
  /** Visually separates a destructive group from the actions above it. */
  readonly separatorBefore?: boolean;
  /** What the action does. Use for a pair of actions that read alike. */
  readonly hint?: string;
  readonly onSelect?: () => void;
  /** Why it cannot run. Presence disables the item and renders the reason.
   *  Prefer one that stays true later ("In use by 2 active campaigns") over
   *  one that describes the implementation ("needs the backend"). */
  readonly disabledReason?: string;
}

export interface ActionMenuProps {
  readonly items: readonly ActionMenuItem[];
  /**
   * Accessible name for the trigger. Required, and it should identify the row:
   * `DataTable`'s `responsive` mounts the table and the card list together, so
   * this control exists twice per row and a generic label makes every query in
   * a test — and every AT announcement — ambiguous.
   */
  readonly triggerLabel: string;
  readonly align?: "start" | "end";
  readonly contentClassName?: string;
  /**
   * Render opened. Forwarded to `Popover`, and there for the same reason it
   * exists there: a visual-regression baseline of a menu that only opens on
   * click would otherwise capture nothing but the trigger.
   */
  readonly defaultOpen?: boolean;
  /** Controlled open state for consumers coordinating sibling popup layers. */
  readonly open?: boolean;
  /** Reports trigger, outside-click, Escape, and item-selection changes. */
  readonly onOpenChange?: (open: boolean) => void;
  /**
   * Size of the trigger button (`TriggerSize`, shared with FilterMenu). `sm`
   * for a compact row of icon controls — a canvas control group, where every
   * sibling is 28px and the default trigger stands 8px taller than the rest.
   */
  readonly triggerSize?: TriggerSize;
  /**
   * Corner of the trigger (C7 shape word). Omitted, it is today's 8px
   * trigger, or the 32px circle inside a `WorkspaceControlGroup` with
   * `railControls` (the trigger is a Button, so it follows the rail).
   * The trigger is always icon-only, so every word is valid.
   */
  readonly triggerShape?: ButtonShape;
  /** Optional ref for consumers that restore focus after opening another layer. */
  readonly triggerRef?: React.Ref<HTMLButtonElement>;
  /** Stable test hook for a trigger that must be found among sibling menus. */
  readonly triggerTestId?: string;
}

export function ActionMenu({
  items,
  triggerLabel,
  align = "end",
  contentClassName,
  defaultOpen,
  open,
  onOpenChange,
  triggerSize = "md",
  triggerShape,
  triggerRef,
  triggerTestId,
}: ActionMenuProps): JSX.Element {
  // Namespaces the description ids to this mounted instance. Ids derived from
  // the row would collide across the two `responsive` mounts, and
  // aria-describedby would resolve to whichever rendered first.
  const scope = React.useId();

  return (
    <Popover
      contentRole="menu"
      align={align}
      {...(defaultOpen !== undefined ? { defaultOpen } : {})}
      {...(open !== undefined ? { open } : {})}
      {...(onOpenChange !== undefined ? { onOpenChange } : {})}
    >
      <PopoverTrigger>
        <Button
          ref={triggerRef}
          variant="ghost"
          size={triggerSize}
          shape={triggerShape}
          iconOnly
          aria-label={triggerLabel}
          {...(triggerTestId !== undefined
            ? { "data-testid": triggerTestId }
            : {})}
          onClick={(event) => event.stopPropagation()}
        >
          <Ellipsis className="h-4 w-4" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-72", contentClassName)}
        onClick={(event) => event.stopPropagation()}
      >
        <ActionMenuItems items={items} scope={scope} />
      </PopoverContent>
    </Popover>
  );
}
